import { IAppLoaderStorage } from '../types/utils/storage-types';
import { HttpClient } from './http-client';
import { appStorage } from '../storage/app-storage';
import { ApiClientMode, ContentType, IApiClient, IHttpClient } from '../types/api/api-types';
import { ApiResponse } from '../../../shared/infrastructure/types/api/api-types';
import { generateBearer } from '../utils/auth/auth-helpers';

class ApiClient implements IApiClient {
  httpClient: IHttpClient;
  private appStorage: IAppLoaderStorage;

  constructor(httpClient: IHttpClient, appStorage: IAppLoaderStorage) {
    this.httpClient = httpClient;
    this.appStorage = appStorage;
  }

  async get<RESP, PARAMS, ERR>(
    path: UrlPath,
    params: PARAMS,
    mode?: ApiClientMode,
    customHeaders?: Record<string, string>,
  ): Promise<ApiResponse<RESP, ERR>> {
    return this.httpClient.get<RESP, ERR>(this.makeReqUrl(path, mode), {
      params: params,
      headers: customHeaders ?? {
        ...this.getAuthHeader(),
      },
    });
  }

  async post<RESP, REQ, ERR>(
    path: UrlPath,
    data?: REQ,
    mode?: ApiClientMode,
    contentType?: ContentType,
    customHeaders?: Record<string, string>,
    queryParams?: object,
  ): Promise<ApiResponse<RESP, ERR>> {
    const contentTypeHeader = contentType ? this.getContentTypeHeader(contentType) : undefined;
    const headers = customHeaders ?? {
      ...this.getAuthHeader(),
      ...contentTypeHeader,
    };
    const config: { headers: Record<string, string>; params?: object } = {
      headers,
    };
    if (queryParams) {
      config.params = queryParams;
    }
    return this.httpClient.post<REQ, RESP, ERR>(this.makeReqUrl(path, mode), data, config);
  }

  async put<REQ, RESP, ERR>(
    path: UrlPath,
    data?: REQ,
    mode?: ApiClientMode,
  ): Promise<ApiResponse<RESP, ERR>> {
    return this.httpClient.put<REQ, RESP, ERR>(this.makeReqUrl(path, mode), data, {
      headers: {
        ...this.getAuthHeader(),
      },
    });
  }

  async delete<RESP, REQ, ERR>(
    path: UrlPath,
    data?: REQ,
    mode?: ApiClientMode,
  ): Promise<ApiResponse<RESP, ERR>> {
    return this.httpClient.delete(this.makeReqUrl(path, mode), data, {
      headers: {
        ...this.getAuthHeader(),
      },
    });
  }

  private makeReqUrl(path: UrlPath, mode?: ApiClientMode): Url {
    let apiUrl: Url | undefined;

    switch (mode) {
      case ApiClientMode.SHARD:
        apiUrl = this.appStorage.apiUrl;
        break;
      case ApiClientMode.SUPREME:
        apiUrl = this.appStorage.supremeUrl;
        break;
      case ApiClientMode.PHONE_BUS:
        apiUrl = this.appStorage.phoneBusUrl;
        break;
      case ApiClientMode.ORIGIN:
        apiUrl = this.appStorage.originDomain;
        break;
      case ApiClientMode.PRODUCT_ORIGIN: {
        const product = this.appStorage.product as { application_url?: string } | undefined;
        apiUrl = (product?.application_url || this.appStorage.originDomain) as Url;
        break;
      }
      default:
        apiUrl = this.appStorage.apiUrl;
    }
    //TODO обработать если пустой урл
    if (!apiUrl) throw Error('No api url');

    return (apiUrl + path) as Url;
  }

  private getAuthHeader(): Record<string, string> {
    if (!this.appStorage.authToken) {
      return {};
    }

    return { Authorization: generateBearer(this.appStorage.authToken) };
  }

  private getContentTypeHeader(type: ContentType): { 'Content-Type': ContentType } {
    return { 'Content-Type': 'multipart/form-data' };
  }
}

const httpClient = new HttpClient();

const appLoaderApiClient = new ApiClient(httpClient, appStorage);

export { appLoaderApiClient };
